import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { EmailDataDto, GenerateLinksRequestDto, GenerateLinksResponseDto, ProductDto, ProductPackageDto, SdkDto, SdkAssetTypeEnum, DriveFileDto, ResponseDto } from '../../../dto'; 

// NG-ZORRO Imports
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input'; 
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzIconModule } from 'ng-zorro-antd/icon';

// Services
import { HttpPackageService } from '../../../services/package/http-package.service';
import { HttpProductService } from '../../../services/product/http-product.service';
import { ToastrService } from 'ngx-toastr';
import { SpinnerService } from '../../../components/spinner/spinner.service';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { HttpEmailService } from '../../../services/email/http-email.service';

@Component({
    selector: 'app-email-composer',
    standalone: true,
    imports: [
        CommonModule, 
        FormsModule,
        NzButtonModule,
        NzCardModule,
        NzTypographyModule,
        NzDividerModule,
        NzListModule,
        NzTagModule,
        NzSpaceModule,
        NzEmptyModule,
        NzSelectModule,
        NzFormModule,
        NzInputModule,
        NzIconModule,
        NzCheckboxModule,
        NzTooltipModule,
    ],
    templateUrl: './email-composer.component.html',
    styleUrls: ['./email-composer.component.scss']
})
export class EmailComposerComponent implements OnInit {
    
    // Selection State
    products: ProductDto[] = [];
    selectedProduct: ProductDto | null = null;
    
    packagesForProduct: ProductPackageDto[] = [];
    selectedPackageIds: number[] = [];

    // Loading States
    isLoadingProducts: boolean = false;
    isLoadingPackages: boolean = false;
    isLoadingLinks: boolean = false;
    isQueueingEmail: boolean = false;

    // Email Preview State
    emailData: EmailDataDto = {
        id: 0,
        customerName: '',
        customerEmail: '',
        customerCcEmail: '',
        ccSales: true,
        ccSupport: true,
        expiry: 7,
        packages: []
    };

    generatedPassword = '';
    SdkAssetTypeEnum = SdkAssetTypeEnum; // Expose to template

    constructor(
        private packageService: HttpPackageService,
        private productService: HttpProductService,
        private emailService: HttpEmailService,
        private toastr: ToastrService,
        private message: NzMessageService,
        private spinnerService: SpinnerService,
    ) {}

    ngOnInit() {
        this.loadProducts();
    }

    loadProducts() {
        this.isLoadingProducts = true;
        this.spinnerService.show("Loading products");
        this.productService.getAllProducts({}).subscribe({
            next: (data) => {
                this.products = data;
                this.isLoadingProducts = false;
                this.spinnerService.hide();
            },
            error: (err) => {
                console.error(err);
                this.toastr.error('Failed to load products');
                this.isLoadingProducts = false;
                this.spinnerService.hide();
            }
        });
    }

    onProductChange(product: ProductDto) {
        this.selectedProduct = product;
        this.selectedPackageIds = []; 
        this.packagesForProduct = []; 
        this.emailData.packages = []; 
        
        if (!this.selectedProduct || !this.selectedProduct.id) return;

        this.isLoadingPackages = true;
        this.packageService.getPackages(this.selectedProduct.id).subscribe({
            next: (data) => {
                this.packagesForProduct = data;
                this.isLoadingPackages = false;

                if (!this.packagesForProduct || this.packagesForProduct.length === 0) {
                    this.toastr.warning("No packages have been configured for " + product.name + ". Please configure packages in the product page");
                }
            },
            error: (err) => {
                console.error(err);
                this.toastr.error('Failed to load packages for this product');
                this.isLoadingPackages = false;
            }
        });
    }

    loadData() {
        if (!this.emailData.customerName) {
            this.toastr.warning("Please enter customer's name.");
            return;
        }
        if (!this.emailData.customerEmail) {
            this.toastr.warning("Please enter customer's email.");
            return;
        }
        if (!this.selectedPackageIds || this.selectedPackageIds.length === 0) {
            this.toastr.warning('Please select at least one package.');
            return;
        }        

        this.isLoadingLinks = true;
        const loadingMsgId = this.message.loading('Generating secure Google Drive links...', { nzDuration: 0 }).messageId;

        var payload: GenerateLinksRequestDto = {
            packageIds: this.selectedPackageIds
        };

        this.packageService.generateLinksForPackages(payload).subscribe({
            next: (resp: GenerateLinksResponseDto) => {
                this.emailData.id = resp.emailId;
                this.emailData.packages = resp.packages;
                this.generatedPassword = resp.generatedPassword;
                
                this.message.remove(loadingMsgId);
                this.toastr.success('Drive links generated successfully!');
                this.isLoadingLinks = false;
            },
            error: (err) => {
                console.error(err);
                this.message.remove(loadingMsgId);
                this.toastr.error('Failed to generate Drive links.');
                this.isLoadingLinks = false;
            }
        });
    }

    isFormValid(): boolean {
        return !!this.emailData.customerName && 
               !!this.emailData.customerEmail && 
               !!this.emailData.expiry && 
               this.emailData.packages.length > 0;
    }

    sendEmail() {
        if (!this.isFormValid()) return;
        console.log('Sending email to backend API...', this.emailData);

        this.isQueueingEmail = true;
        this.emailService.saveEmail(this.emailData).subscribe({
            next: (resp: ResponseDto) => {
                if (resp.success === true) {
                    this.toastr.success('Email queued successfully');
                } else {
                    this.toastr.success('Failed to queue email: ' + resp.error);
                }                
                this.isQueueingEmail = false;
            },
            error: (err) => {
                console.error(err);
                this.toastr.error('Failed to queue email.');
                this.isQueueingEmail = false;
            }
        });
    }

    // Helper method to filter assets by type for the email template
    getAssetsList(sdk: SdkDto, type: SdkAssetTypeEnum): DriveFileDto[] {
        return sdk.assets?.filter(a => a.assetType === type).map(a => a.driveFile!).filter(f => !!f) || [];
    }
}